import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { ClubOverviewService } from '~features/club-overview/club-overview.service';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { NotificationType } from '~core/enum';
import { Club } from '~core/models';
import { ClubService, DefaultNotificationService, StorageService } from '~core/services';
import { ClubTranslationsService } from '~shared/translations';


/**
 * Component that displays the form and communicates with the Club API
 */
@UntilDestroy()
@Component({
  selector: 'app-modules-form',
  templateUrl: './modules-form.component.html',
  styleUrls: ['../../../pages/club-settings/club.component.scss']
})
export class ModulesFormComponent implements OnInit, OnDestroy {
  public clubForm: UntypedFormGroup;
  public spinnerName: 'editClubModules';
  @Input() public selectedClub: Club;

  constructor(
    public clubService: ClubService,
    public clubOverviewService: ClubOverviewService,
    private notificationService: DefaultNotificationService,
    public clubTranslations: ClubTranslationsService,
    public storage: StorageService,
    public buttonsTranslations: ButtonsTranslations,
    private spinnerService: SpinnerService,
    private formBuilder: UntypedFormBuilder
  ) { }

  public ngOnInit(): void {
    this.loadData();
  }

  public ngOnDestroy(): void { }

  /**
   * Fetch the club-overview modules data via the url parameter and fill the club-overview form.
   */
  public loadData(): void {
    this.clubForm = this.formBuilder.group({
      id: [this.selectedClub.id],
      status: [null]
    });
  }

  /**
   * Handles updating of the club-overview. If form is valid submit the form otherwise
   * displays errors.
   */
  public updateClub(): any {
    let anyErrors: boolean = false;
    let num = 0;
    const numberOfResponses: BehaviorSubject<string> = new BehaviorSubject<string>('add');
    const responseObserver = numberOfResponses.asObservable();

    responseObserver.subscribe(value => {
      if (value === 'add') {
        num++;
      } else if (value === 'remove') {
        num--;
      }

      if (num === 0) {
        this.spinnerService.hide(this.spinnerName);
        this.clubOverviewService.toggleAllModal(false);
        if (anyErrors) {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.clubTranslations.i18nErrorClubUpdate
          });
        } else {
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: this.clubTranslations.i18nSuccessClubUpdate
          });
        }
      }
    });

    this.spinnerService.show(this.spinnerName);
    this.selectedClub.modules.forEach(el => {
      if (el.checked && el.status === 'inactive') {
        numberOfResponses.next('add');
        this.clubForm.patchValue({ id: el.id, status: 'active' });
        this.clubService.updateModules(this.clubForm.value).pipe(untilDestroyed(this))
          .subscribe({
            next: res => {
              numberOfResponses.next('remove');
              el.status = 'active';
              el.checked = true;
            },
            error: (error: Error) => {
              anyErrors = true;
              numberOfResponses.next('remove');
            }
          });
      }

      if (!el.checked && el.status === 'active') {
        numberOfResponses.next('add');
        this.clubForm.patchValue({ id: el.id, status: 'inactive' });
        this.clubService.updateModules(this.clubForm.value).pipe(untilDestroyed(this))
          .subscribe({
            next: res => {
              numberOfResponses.next('remove');
              el.status = 'inactive';
              el.checked = null;
            },
            error: (error: Error) => {
              anyErrors = true;
              numberOfResponses.next('remove');
            }
          });
      }
    });
    numberOfResponses.next('remove');
  }
}
